/**
 * Wave 1u9f — Public page for company-level questionnaires.
 *
 * Mounted on `/q/company/:token`. Lets a company contact fill the
 * questionnaire without any auth. The shell intentionally mirrors
 * `QuestionnairePublic.jsx` (apprenant-side) for visual consistency.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Loader2, ClipboardList, Building2 } from 'lucide-react';
import axios from 'axios';
import { computeCompletion, isQuestionVisible } from '../lib/questionnaireLogic';

const publicApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

export default function CompanyQuestionnairePublic() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get(`/public/company-questionnaires/${token}`);
        setInfo(data);
        // Ce que l'organisme a pré-rempli : posé dans les champs, et
        // **toujours modifiable**. C'est toute la différence entre demander
        // de rédiger et demander de confirmer — l'étude de besoin porte
        // trente-trois questions, et elle partait vierge.
        if (data.prerempli && Object.keys(data.prerempli).length) {
          setAnswers(data.prerempli);
        }
        if (data.already_submitted) setSubmitted(true);
      } catch (err) {
        setError(err?.response?.data?.detail || 'Lien invalide ou expiré.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const setAnswer = (qid, value) => setAnswers((prev) => ({ ...prev, [qid]: value }));

  // Wave 1u9j — completion + visibility (mirrors backend logic)
  const completion = useMemo(() => computeCompletion(info?.questions || [], answers || {}), [info, answers]);

  const submit = async () => {
    if (!info) return;
    if (completion.missingRequired.length) {
      setError(`Veuillez répondre à toutes les questions obligatoires (${completion.missingRequired.length} restante(s)).`);
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await publicApi.post(`/public/company-questionnaires/${token}/submit`, { answers });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Envoi impossible.');
    }
    setSubmitting(false);
  };

  const branding = info?.branding || {};
  const primary = branding.primary || '#4F46E5';

  if (loading) {
    return (
      <div data-testid="cq-public-loading" className="min-h-screen bg-[#FAFAFE] flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-[#4F46E5]" />
      </div>
    );
  }

  if (error && !info) {
    return (
      <div data-testid="cq-public-error" className="min-h-screen bg-[#FAFAFE] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-[#EFF0F6] p-8 max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-[#FEF2F2] text-[#B91C1C] mx-auto flex items-center justify-center">
            <ClipboardList className="w-6 h-6" />
          </div>
          <h1 className="text-[18px] font-bold text-[#1A1B2E] mt-4">Lien indisponible</h1>
          <p className="text-[13.5px] text-[#6B6C85] mt-1.5">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    const submittedDate = info?.filled_at ? new Date(info.filled_at).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' }) : null;
    return (
      <div data-testid="cq-public-submitted" className="min-h-screen bg-[#FAFAFE] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-[#EFF0F6] p-8 max-w-md text-center">
          <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center text-white" style={{ backgroundColor: primary }}>
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h1 className="text-[20px] font-bold text-[#1A1B2E] mt-4">Réponses enregistrées</h1>
          <p className="text-[13.5px] text-[#6B6C85] mt-1.5">Ce questionnaire a déjà été complété et ne peut plus être modifié.</p>
          {submittedDate && <p className="text-[12px] text-[#8E8FA5] mt-2">Rempli le {submittedDate}.</p>}
          <p className="text-[12px] text-[#8E8FA5] mt-3">Si vous pensez avoir besoin de le modifier, contactez votre prestataire de formation.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cq-public-page" className="min-h-screen bg-[#FAFAFE] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-[#EFF0F6] overflow-hidden mb-4">
          <div className="px-7 py-5 flex items-center gap-4" style={{ background: `linear-gradient(135deg, ${primary}10, ${primary}05)` }}>
            {branding.logo_b64
              ? <img src={branding.logo_b64} alt="" className="w-12 h-12 rounded-lg object-contain bg-white border border-[#EFF0F6]" />
              : <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold" style={{ backgroundColor: primary }}><ClipboardList className="w-5 h-5" /></div>}
            <div>
              <div className="text-[11.5px] font-semibold uppercase tracking-wider" style={{ color: primary }}>{branding.org_name || 'OFManager'}</div>
              <h1 className="text-[20px] font-bold text-[#1A1B2E] leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{info.title}</h1>
            </div>
          </div>
          {info.company_name && (
            <div className="px-7 py-3 border-t border-[#EFF0F6] flex items-center gap-2 text-[12.5px] text-[#4A4B63]">
              <Building2 className="w-3.5 h-3.5" /> Pour l'entreprise : <strong className="text-[#1A1B2E]">{info.company_name}</strong>
            </div>
          )}
          {/* On le dit : des reponses posees sans explication se lisent comme
              les reponses de quelqu'un d'autre, et on hesite a les corriger. */}
          {info.prerempli && Object.keys(info.prerempli).length > 0 && (
            <div className="px-7 py-3 border-t border-[#EFF0F6] text-[12.5px]"
                 style={{ backgroundColor: `${primary}0D`, color: '#4A4B63' }}>
              Certaines réponses ont été pré-remplies par
              {info.prerempli_par ? ` ${info.prerempli_par}` : ' votre organisme de formation'}
              {' '}— <strong>vérifiez-les, corrigez ce qui ne va pas, complétez le reste.</strong>
            </div>
          )}
          {info.description && (
            <div className="px-7 py-3 border-t border-[#EFF0F6] text-[13px] text-[#4A4B63] whitespace-pre-line">{info.description}</div>
          )}
        </div>

        {/* Questions */}
        <div className="bg-white rounded-2xl shadow-sm border border-[#EFF0F6] overflow-hidden">
          {/* Wave 1u9j — sticky progress bar */}
          <div className="sticky top-0 z-10 bg-white border-b border-[#EFF0F6] px-7 py-3">
            <div className="flex items-center justify-between gap-3 mb-1.5">
              <div className="text-[12px] text-[#4A4B63]">
                <span className="font-bold tabular-nums text-[#1A1B2E]">{completion.reqDone}</span>
                <span className="text-[#6B6C85]"> / {completion.totalRequired || '0'} obligatoires</span>
                {completion.optDone > 0 && (
                  <span className="text-[#6B6C85]"> · {completion.optDone} facultatives</span>
                )}
              </div>
              <span className="text-[12px] font-bold tabular-nums" style={{ color: completion.allRequiredOk ? '#0F766E' : primary }}>
                {completion.pct}%
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-[#F6F7FB] overflow-hidden">
              <div
                data-testid="cq-public-progress-bar"
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${completion.pct}%`,
                  background: completion.allRequiredOk
                    ? 'linear-gradient(90deg, #14B8A6, #0F766E)'
                    : `linear-gradient(90deg, ${primary}80, ${primary})`,
                }}
              />
            </div>
          </div>

          <div className="p-7 space-y-5">
            {(info.questions || []).filter((q) => isQuestionVisible(q, info.questions || [], answers)).map((q, i) => (
              <QField key={q.id} idx={i + 1} question={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} primary={primary} />
            ))}

            {error && (
              <div className="text-[12.5px] text-[#B91C1C] bg-[#FEF2F2] rounded-lg px-3 py-2">{error}</div>
            )}

            <button
              data-testid="cq-public-submit"
              type="button"
              onClick={submit}
              disabled={submitting}
              className="w-full h-12 rounded-xl text-white font-semibold transition-opacity disabled:opacity-60"
              style={{ backgroundColor: primary }}
            >
              {submitting ? (<><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Envoi…</>) : 'Envoyer mes réponses'}
            </button>
          </div>
        </div>

        <p className="text-center text-[11px] text-[#8E8FA5] mt-4">
          Vos réponses sont confidentielles et ne sont accessibles qu'à {branding.org_name || 'votre prestataire'}.
        </p>
      </div>
    </div>
  );
}

function QField({ idx, question, value, onChange, primary }) {
  const selStyle = { borderColor: primary, backgroundColor: `${primary}10` };
  const accent = { accentColor: primary };
  const label = (
    <label className="block text-[13.5px] font-semibold text-[#1A1B2E] mb-2">
      {idx}. {question.label}
      {question.required && <span className="text-[#EF4444] ml-1">*</span>}
    </label>
  );

  if (question.type === 'long_text') {
    return (
      <div data-testid={`cq-q-${question.id}`}>
        {label}
        <textarea rows={4} value={value || ''} onChange={(e) => onChange(e.target.value)}
          className="w-full px-3.5 py-2.5 rounded-xl border border-[#EFF0F6] bg-white text-[13.5px] focus:outline-none resize-none"
          style={{ borderColor: value ? primary : undefined }} />
      </div>
    );
  }
  if (question.type === 'single') {
    return (
      <div data-testid={`cq-q-${question.id}`}>
        {label}
        <div className="space-y-1.5">
          {(question.options || []).map((opt) => {
            const sel = value === opt;
            return (
              <label key={opt} className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border cursor-pointer" style={sel ? selStyle : { borderColor: '#EFF0F6' }}>
                <input type="radio" name={question.id} value={opt} checked={sel} onChange={() => onChange(opt)} style={accent} />
                <span className="text-[13.5px] text-[#1A1B2E]">{opt}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  }
  if (question.type === 'multi') {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => onChange(arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt]);
    return (
      <div data-testid={`cq-q-${question.id}`}>
        {label}
        <div className="space-y-1.5">
          {(question.options || []).map((opt) => {
            const sel = arr.includes(opt);
            return (
              <label key={opt} className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border cursor-pointer" style={sel ? selStyle : { borderColor: '#EFF0F6' }}>
                <input type="checkbox" checked={sel} onChange={() => toggle(opt)} style={accent} />
                <span className="text-[13.5px] text-[#1A1B2E]">{opt}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  }
  if (question.type === 'scale') {
    const min = question.scale_min ?? 1;
    const max = question.scale_max ?? 5;
    const items = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    return (
      <div data-testid={`cq-q-${question.id}`}>
        {label}
        <div className="flex items-center gap-2 flex-wrap">
          {items.map((n) => {
            const sel = value === n;
            return (
              <button type="button" key={n} onClick={() => onChange(n)}
                style={sel ? { borderColor: primary, backgroundColor: primary, color: '#fff' } : undefined}
                className={`w-11 h-11 rounded-xl border text-[14px] font-bold ${sel ? '' : 'border-[#EFF0F6] bg-white text-[#4A4B63]'}`}>
                {n}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  // default: text
  return (
    <div data-testid={`cq-q-${question.id}`}>
      {label}
      <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 px-3.5 rounded-xl border border-[#EFF0F6] bg-white text-[13.5px] focus:outline-none"
        style={{ borderColor: value ? primary : undefined }} />
    </div>
  );
}
